import { Component, Prop, h } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'sn-key-features-type-1';

/**
 * Key features section presenting product benefits as text-only cards.
 * Header (heading + subtitle) above a 3-column responsive grid of grey background cards.
 * Wider 3-column layout for concise feature lists; use type-2 for the 2-column variant with more breathing room per card.
 *
 * @category SignNow
 * @slot heading - {content} <me-heading variant="4" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Section heading for key features of {keyword}. 6-10 words.
 * @slot subtitle - {content} <me-text variant="large"> - Introductory paragraph for the key features section. You MUST write 20-30 words.
 * @example
 *   <sn-key-features-type-1
 *     cards='[{"title":"Powerful & secure","text":"eSign documents seamlessly."},{"title":"Cost-effective","text":"Lower rates per invite."}]'
 *   >
 *     <me-heading slot="heading" variant="4" tag="h2">Key features &amp; benefits</me-heading>
 *     <me-text slot="subtitle" variant="large">Understanding the key elements.</me-text>
 *   </sn-key-features-type-1>
 */
@Component({
  tag: 'sn-key-features-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class DpcKeyFeaturesThreeCol {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Feature cards displayed in a 3-column grid.
   * @content
   * @type {array}
   * @item.type {object}
   * @item.property {string} title - Feature name. 1-4 words.
   * @item.property {string} text - Benefit-focused description of this feature for {keyword}. You MUST write 25-40 words.
   * @item.required title
   * @item.content title text
   * @minItems 6
   * @maxItems 6
   */
  @Prop({ mutable: true }) cards?: { title?: string; text?: string }[] | string;

  /**
   * Legacy / raw-HTML fallback. For new usage prefer slots + `cards` prop.
   * @dynamic
   * @type {object}
   * @property {string} raw_html - Raw HTML content (replaces entire block)
   * @property {string} title - Fallback heading (use slot="heading" instead)
   * @property {string} subtitle - Fallback subtitle (use slot="subtitle" instead)
   * @property {array} cards - Fallback feature cards (use cards prop instead)
   */
  @Prop({ mutable: true }) values?:
    | {
        title?: string;
        subtitle?: string;
        raw_html?: string;
        cards?: { title?: string; text?: string }[];
      }
    | string;

  componentWillLoad() {
    this.values = parseJsonProp(this.values);
    this.cards = parseJsonProp(this.cards);
  }

  render() {
    const values = typeof this.values === 'object' ? this.values : undefined;

    if (values?.raw_html) {
      return <me-section class={COMPONENT_NAME} yGap="md" yGapLg="xl" innerHTML={values.raw_html}></me-section>;
    }

    const cards = Array.isArray(this.cards)
      ? this.cards
      : (Array.isArray(values?.cards) ? values.cards : []);

    return (
      <me-section class={COMPONENT_NAME} yGap="3xl" yGapLg="5xl">
        <me-row direction="column" directionLg="column" yGap="4xl" yGapLg="4xl">
          <me-column spanLg="12">
            <div class={`${COMPONENT_NAME}__header`}>
              <slot name="heading"></slot>
              <slot name="subtitle"></slot>
            </div>
          </me-column>
          {cards.length > 0 && (
            <me-column spanLg="12">
              <div class={`${COMPONENT_NAME}__grid`}>
                {cards.map((card) => (
                  <div class={`${COMPONENT_NAME}__card`}>
                    {card.title && <me-heading variant="5" tag="h3" class={`${COMPONENT_NAME}__card-title`}>{card.title}</me-heading>}
                    {card.text && <me-text class={`${COMPONENT_NAME}__card-text`}>{card.text}</me-text>}
                  </div>
                ))}
              </div>
            </me-column>
          )}
        </me-row>
      </me-section>
    );
  }
}
